import React from 'react';
import {Box, Grid, Typography} from '@mui/material';
import {PieChart, Pie, Cell, ResponsiveContainer, Tooltip} from 'recharts';
import SchoolDashboardShell from '../components/SchoolDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {NAVY, PageHeader, BandChip} from '../components/widgets.comp';
import {READINESS_BAND_COLORS, CONTEXT_BAND_COLORS, RISK_BAND_COLORS} from '../../../utils/constants';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, cellText} from '../schoolData';
import {isSuppressedCycle, SUPPRESSED_COPY} from '../../DashboardCommon/dashboardCards';

const BLUE = '#3B6FA0';
const GREY = '#D1D5DB';
const DARK = '#1F2937';

/* Slide 48 — Child Online Safety (COS) Reassessment Overview. Dummy data —
   the school has not completed reassessment yet, so the deck frames this as
   a preview of the confirmed COS Movement view. Structure and figures are
   copied from Partner Dashboard's already-built Reassessment page
   (src/pages/PartnerDashboard/pages/Reassessment.page.jsx), which uses this
   exact same 2025→2026 comparison layout — School's mockup image matches it
   number-for-number. Colors come from that existing page's own palette and
   this dashboard's rubric bands, not the mockup image's own sample colors
   (orange banner, gold numbers, orange bars). */
const RISK_EXPERIENCE_COLORS = {
    'No or Minimal Risk': RISK_BAND_COLORS.Monitor,
    'Low Risk': RISK_BAND_COLORS.Watch,
    'Moderate Risk': RISK_BAND_COLORS.Elevated,
    'High Risk': RISK_BAND_COLORS['Priority Concern'],
};

const rubricColor = (label) =>
    READINESS_BAND_COLORS[label] || CONTEXT_BAND_COLORS[label] || RISK_EXPERIENCE_COLORS[label] || RISK_BAND_COLORS[label] || '#6B7280';

/* Live data (SD-48-02 … 07), in this page's own layout:
     score cards   SD.S_17.C_2 / C_3 / C_4 (MovementCard: baseline → reassess, delta, benchmarks)
     duration      SD.S_17.C_5   ·   donut C_6 (+ Assessed / Not assessed)   ·   Matched Cohort C_7
     bars          SD.S_17.C_8 (Unit · Type · Assessed · Registered · Rate % · Status)
   Year labels come from the card ("2026 (cycle 1)"). Until a school reassesses the
   cards are preview_only: the banner shows and the reassessment side reads "Pending". */
const THRESHOLD_PCT = 50;
const numeric = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const whole = (v) => Math.trunc(Number(v));

const toScore = (title, card) => {
    const base = card?.baseline || {};
    const re = card?.reassess || {};
    const unit = card?.unit === 'pp' ? '%p' : (card?.unit || '');
    const pct = card?.unit === 'pp' ? '%' : '';
    // A side under the n floor (suppressed) shows no value or band; the delta needs both sides.
    const supBase = isSuppressedCycle(base), supRe = isSuppressedCycle(re);
    const hasRe = !supRe && numeric(re.value);
    const delta = supBase || supRe ? null : (numeric(card?.delta) ? whole(card.delta) : (hasRe && numeric(base.value) ? whole(re.value) - whole(base.value) : null));
    const bench = (re_) => {
        const b = (card?.benchmarks || []).find((x) => re_.test(x?.name || ''));
        if (!b) return '—';
        const useRe = hasRe && numeric(b.reassess) && !b.suppressed_reassess;
        if (useRe) return `${whole(b.reassess)}${pct}`;
        return !b.suppressed_baseline && numeric(b.baseline) ? `${whole(b.baseline)}${pct}` : '—';
    };
    return {
        title,
        baseLabel: base.label || 'Baseline',
        reLabel: re.label || 'Reassessment',
        y2025: !supBase && numeric(base.value) ? `${whole(base.value)}${pct}` : '—',
        band2025: supBase ? null : base.band,
        y2026: hasRe ? `${whole(re.value)}${pct}` : (supRe ? '—' : 'Pending'),
        band2026: supRe ? null : re.band,
        delta: delta === null ? '' : `${delta >= 0 ? '+' : ''}${delta} ${unit}`,
        suppressed: supBase || supRe,
        nationalAvg: bench(/national/i),
        globalAvg: bench(/global/i),
    };
};

const PreviewBanner = () => (
    <Box sx={{
        mb: 3, p: {xs: 2, md: 2.5}, borderRadius: '0 8px 8px 0',
        backgroundColor: 'var(--brand-surface-subtle, #F3F4F6)', border: '1px solid var(--brand-border, #E5E7EB)',
        borderLeft: `4px solid ${NAVY}`,
    }}>
        <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY, mb: 0.5}}>
            Preview only: Your school has not completed reassessment yet.
        </Typography>
        <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>
            The figures below show how the confirmed COS Movement view will read once reassessment is complete. We recommend reassessing about one year after the baseline assessment to see how far students have progressed in readiness, cyber risk experience, and the context and support around them.
        </Typography>
    </Box>
);

const CompositeScoreCard = ({title, baseLabel, reLabel, y2025, band2025, y2026, band2026, delta, nationalAvg, globalAvg, suppressed}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 2.5, height: '100%',
    }}>
        <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, mb: suppressed ? 0.5 : 2}}>{title}</Typography>
        {suppressed && <Typography sx={{fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic', mb: 1.5}}>{SUPPRESSED_COPY}</Typography>}

        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5}}>
            <Box sx={{textAlign: 'center'}}>
                <Typography sx={{fontSize: '11px', color: '#9CA3AF', mb: 0.5}}>{baseLabel}</Typography>
                <Typography sx={{fontSize: '22px', fontWeight: 800, color: '#6A7282'}}>{y2025}</Typography>
                {band2025 && <Box sx={{mt: 0.5}}><BandChip label={band2025} color={rubricColor(band2025)}/></Box>}
            </Box>
            <Typography sx={{fontSize: '20px', color: '#CBD5E1'}}>&rarr;</Typography>
            <Box sx={{textAlign: 'center'}}>
                <Typography sx={{fontSize: '11px', color: '#9CA3AF', mb: 0.5}}>{reLabel}</Typography>
                <Typography sx={{fontSize: '22px', fontWeight: 800, color: NAVY}}>{y2026}</Typography>
                {band2026 && <Box sx={{mt: 0.5}}><BandChip label={band2026} color={rubricColor(band2026)}/></Box>}
            </Box>
            <Box sx={{
                fontSize: '13px', fontWeight: 700, color: delta.startsWith('+') ? '#1B6B55' : '#E04535',
                whiteSpace: 'nowrap',
            }}>
                {delta}
            </Box>
        </Box>

        <Box sx={{display: 'flex', gap: 2, mt: 2, pt: 1.5, borderTop: '1px solid var(--brand-border, #E5E7EB)'}}>
            <Typography sx={{fontSize: '12px', color: '#9CA3AF'}}>National Avg. {nationalAvg}</Typography>
            <Typography sx={{fontSize: '12px', color: '#9CA3AF'}}>Global Avg. {globalAvg}</Typography>
        </Box>
    </Box>
);

const ReassessmentDurationBox = ({card}) => {
    // "2026-09-16 – 2026-09-16" → Start / End, as the slide lays it out; otherwise as given ("Pending").
    const text = cellText(card?.kpi_value);
    const parts = text.split(/\s+[–-]\s+/);
    return (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 2.5, mb: 3,
    }}>
        <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 1}}>
            Reassessment Duration
        </Typography>
        <Typography sx={{fontSize: '14px', color: '#374151'}}>
            {parts.length === 2
                ? <><strong>Start Date:</strong> {parts[0]} &nbsp;&nbsp;-&nbsp;&nbsp; <strong>End Date:</strong> {parts[1]}</>
                : text}
        </Typography>
    </Box>
    );
};

const CompletionDonut = ({completion, cohort}) => {
    const rate = numeric(completion?.kpi_value) ? Math.min(100, Math.max(0, whole(completion.kpi_value))) : null;
    const info = (name) => (completion?.kpi_add_infos || []).find((i) => String(i?.info_name || '').toLowerCase() === name)?.info_value;
    const assessed = numeric(info('assessed')) ? whole(info('assessed')) : null;
    const notAssessed = numeric(info('not assessed')) ? whole(info('not assessed')) : null;
    const DONUT_DATA = rate === null ? [] : [
        {name: 'Assessed', value: assessed !== null ? assessed : rate},
        {name: 'Not Assessed', value: notAssessed !== null ? notAssessed : 100 - rate},
    ];
    const cohortText = numeric(cohort?.kpi_value) ? `${whole(cohort.kpi_value)}%` : cellText(cohort?.kpi_value);
    return (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 2.5, height: '100%',
    }}>
        <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 1}}>
            Total Assessment Completion Rate
        </Typography>
        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            {rate === null ? (
                <Typography sx={{fontSize: '26px', fontWeight: 800, color: NAVY, my: 3}}>{cellText(completion?.kpi_value)}</Typography>
            ) : (<>
            <Box sx={{width: '100%', maxWidth: 220, height: 180}}>
                <ResponsiveContainer>
                    <PieChart>
                        <Pie data={DONUT_DATA} dataKey="value" innerRadius={50} outerRadius={72} paddingAngle={1}>
                            <Cell fill={BLUE}/>
                            <Cell fill={GREY}/>
                        </Pie>
                        <Tooltip/>
                    </PieChart>
                </ResponsiveContainer>
            </Box>
            <Box sx={{display: 'flex', gap: 3, mt: 1}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                    <Box sx={{width: 10, height: 10, borderRadius: '2px', backgroundColor: BLUE}}/>
                    <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Assessed ({DONUT_DATA[0].value})</Typography>
                </Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                    <Box sx={{width: 10, height: 10, borderRadius: '2px', backgroundColor: GREY}}/>
                    <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Not Assessed ({DONUT_DATA[1].value})</Typography>
                </Box>
            </Box>
            </>)}
            <Typography sx={{fontSize: '13px', color: '#374151', mt: 2}}>
                <strong>Matched Cohort:</strong> {cohortText}
            </Typography>
        </Box>
    </Box>
    );
};

const CompletionBarRow = ({label, indent, pct, fraction, status}) => {
    const met = status === 'Met';
    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.75}}>
            <Typography sx={{
                fontSize: '13px', fontWeight: indent ? 500 : 700, color: indent ? '#374151' : NAVY,
                width: 90, flexShrink: 0, pl: indent ? 1.5 : 0,
            }}>
                {label}
            </Typography>
            <Box sx={{position: 'relative', flex: 1, height: 14, borderRadius: '7px', backgroundColor: 'var(--brand-surface-muted, #F3F4F6)'}}>
                <Box sx={{
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`,
                    backgroundColor: met ? BLUE : DARK, borderRadius: '7px',
                }}/>
                <Box sx={{
                    position: 'absolute', left: `${THRESHOLD_PCT}%`, top: -3, bottom: -3, width: '2px',
                    backgroundColor: NAVY,
                }}/>
            </Box>
            <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY, width: 90, flexShrink: 0, textAlign: 'right'}}>
                {pct}%{fraction ? ` (${fraction})` : ''}
            </Typography>
            <Box sx={{width: 90, flexShrink: 0, display: 'flex', justifyContent: 'flex-end'}}>
                {status && <BandChip label={status} color={met ? '#1B6B55' : DARK}/>}
            </Box>
        </Box>
    );
};

const CompletionByGradeClass = ({rows}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 2.5, height: '100%',
    }}>
        <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 2}}>
            Reassessment Completion Rate by Class (50% Threshold)
        </Typography>
        {rows.length === 0 && (
            <Typography sx={{fontSize: '13.5px', color: '#6A7282', py: 3, textAlign: 'center'}}>Pending — shown once reassessment starts.</Typography>
        )}
        {rows.map((row, i) => (
            <CompletionBarRow key={`${row.label}-${i}`} {...row}/>
        ))}
    </Box>
);

const REASSESSMENT_SECTION_CODE = 'S_17';
const CARD_SECTIONS = [REASSESSMENT_SECTION_CODE];

const ReassessmentPage = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const card = (c) => findCard(dashboardData, REASSESSMENT_SECTION_CODE, c);
    const COMPOSITE_SCORES = [
        toScore('COS Composite Score', card('C_2')),
        toScore('Cyber Risk Experience', card('C_3')),
        toScore('Context & Support', card('C_4')),
    ];
    const preview = ['C_2', 'C_3', 'C_4', 'C_5', 'C_6', 'C_7', 'C_8'].some((c) => card(c)?.preview_only === true);

    // C_8 columns: Unit · Type · Assessed · Registered · Rate % · Status (classes indent under their grade).
    const cols = card('C_8')?.table_objects || [];
    const col = (name) => cols.find((c) => String(c.column_name || '').trim().toLowerCase() === name);
    const unit = col('unit'), type = col('type'), assessed = col('assessed'), registered = col('registered'), rate = col('rate %'), status = col('status');
    const COMPLETION_ROWS = (unit?.values || []).map((label, i) => ({
        label,
        indent: /class/i.test(String(type?.values?.[i] || '')),
        pct: numeric(rate?.values?.[i]) ? whole(rate.values[i]) : 0,
        fraction: numeric(assessed?.values?.[i]) && numeric(registered?.values?.[i]) ? `${whole(assessed.values[i])}/${whole(registered.values[i])}` : '',
        status: status?.values?.[i] || '',
    }));

    if (loading) {
        return (
            <SchoolDashboardShell>
                <CircularLoader />
            </SchoolDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <SchoolDashboardShell>
                <NoDataAvailable />
            </SchoolDashboardShell>
        );
    }

    return (
    <SchoolDashboardShell>
        <PageHeader
            title="Child Online Safety (COS) Reassessment Overview"
            subtitle="How far have students progressed since the baseline?"
        />

        {preview && <PreviewBanner/>}

        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>
            Assessment Scores
        </Typography>
        <Grid container spacing={2.5} alignItems="stretch" sx={{mb: 4}}>
            {COMPOSITE_SCORES.map((s) => (
                <Grid item xs={12} md={4} key={s.title}>
                    <CompositeScoreCard {...s}/>
                </Grid>
            ))}
        </Grid>

        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>
            Completion Status
        </Typography>
        <ReassessmentDurationBox card={card('C_5')}/>
        <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} md={5}>
                <CompletionDonut completion={card('C_6')} cohort={card('C_7')}/>
            </Grid>
            <Grid item xs={12} md={7}>
                <CompletionByGradeClass rows={COMPLETION_ROWS}/>
            </Grid>
        </Grid>
    </SchoolDashboardShell>
    );
};

export default ReassessmentPage;
